/**
 * Debug panel for topology visualization modes.
 *
 * Visible when debug mode is active. Each mode is a radio-style toggle —
 * only one can be active at a time. Clicking the active mode disables it.
 */

import { useStore } from "../app/store.ts";
import type { PrimitiveSymmetryDebugAxis } from "../app/storeTypes.ts";
import type { TopologyVizMode } from "../viewport/topologyViz.ts";
import { Button } from "./Button.tsx";
import { DraggablePanel } from "./DraggablePanel.tsx";
import { usePanelSelection } from "./floatingPanelStore.ts";
import {
  COLOR_TEXT_DIM,
  COLOR_TEXT_MUTED,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_TINY,
  Z_DEBUG,
} from "./tokens.ts";

const MODES: { id: TopologyVizMode; label: string; description: string }[] = [
  {
    id: "stretch",
    label: "Stretch / Compress",
    description: "edge length vs rest pose",
  },
  {
    id: "normals",
    label: "Normal Orientation",
    description: "front / back facing",
  },
  {
    id: "normalDeviation",
    label: "Normal Deviation",
    description: "smooth vs geometric",
  },
  {
    id: "density",
    label: "Vertex Density",
    description: "polygon distribution",
  },
];

export function TopologyVizPanel() {
  const debugEnabled = useStore((s) => s.debugEnabled);
  const panelOpen = useStore((s) => s.debugPanelOpen.topologyViz);
  const activeMode = useStore((s) => s.activeTopologyViz);
  const setMode = useStore((s) => s.setActiveTopologyViz);
  const primitiveSymmetryDebugAxis = useStore(
    (s) => s.primitiveSymmetryDebugAxis,
  );
  const setPrimitiveSymmetryDebugAxis = useStore(
    (s) => s.setPrimitiveSymmetryDebugAxis,
  );
  const togglePanel = useStore((s) => s.toggleDebugPanel);
  const sel = usePanelSelection("debug-topology-viz", () =>
    togglePanel("topologyViz"),
  );

  if (!debugEnabled || !panelOpen) return null;

  return (
    <DraggablePanel
      id="debug-topology-viz"
      defaultPosition={{ top: 8, left: 8 }}
      zIndex={Z_DEBUG}
      collapsible
      selectionColor="#ff0"
      {...sel}
      style={{ minWidth: 220 }}
      header={
        <span
          style={{
            color: "#ff0",
            fontWeight: "bold",
            fontFamily: "monospace",
            fontSize: FONT_SIZE_TINY,
          }}
        >
          TOPOLOGY
        </span>
      }
    >
      <div
        style={{
          background: "rgba(0,0,0,0.85)",
          color: COLOR_TEXT_SECONDARY,
          fontFamily: "monospace",
          fontSize: FONT_SIZE_TINY,
          padding: "6px 10px",
          borderRadius: "0 0 6px 6px",
          pointerEvents: "auto",
          lineHeight: 1.8,
        }}
      >
        {MODES.map((mode) => (
          <ModeRow
            key={mode.id}
            mode={mode}
            active={activeMode === mode.id}
            onToggle={() => setMode(activeMode === mode.id ? null : mode.id)}
          />
        ))}
        {activeMode && (
          <div
            style={{
              color: COLOR_TEXT_DIM,
              marginTop: 4,
              borderTop: "1px solid #333",
              paddingTop: 2,
              fontSize: 9,
            }}
          >
            {activeMode === "density"
              ? "static — rest pose only"
              : "live — updates each frame"}
          </div>
        )}
        <div
          style={{
            color: COLOR_TEXT_DIM,
            marginTop: 6,
            borderTop: "1px solid #333",
            paddingTop: 4,
            fontSize: 9,
          }}
        >
          primitives: symmetry debug in object-mode wireframe
        </div>
        <AxisRow
          axis={primitiveSymmetryDebugAxis}
          onSetAxis={setPrimitiveSymmetryDebugAxis}
        />
      </div>
    </DraggablePanel>
  );
}

function ModeRow({
  mode,
  active,
  onToggle,
}: {
  mode: { id: TopologyVizMode; label: string; description: string };
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        cursor: "pointer",
        color: active ? "#0f0" : COLOR_TEXT_MUTED,
        userSelect: "none",
      }}
    >
      <input
        type="radio"
        name="topology-viz"
        checked={active}
        onChange={onToggle}
        style={{ accentColor: "#0f0", cursor: "pointer" }}
        data-no-drag
      />
      <span>
        {mode.label}
        <span style={{ color: COLOR_TEXT_DIM, marginLeft: 4 }}>
          ({mode.description})
        </span>
      </span>
    </label>
  );
}

function AxisRow({
  axis,
  onSetAxis,
}: {
  axis: PrimitiveSymmetryDebugAxis | null;
  onSetAxis: (axis: PrimitiveSymmetryDebugAxis | null) => void;
}) {
  const axes: PrimitiveSymmetryDebugAxis[] = ["x", "y", "z"];
  return (
    <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
      {axes.map((nextAxis) => {
        const active = axis === nextAxis;
        return (
          <Button
            key={nextAxis}
            onClick={() => onSetAxis(active ? null : nextAxis)}
            data-no-drag
            variant="ghost"
            size="xs"
            style={{
              border: "1px solid #333",
              background: active
                ? "rgba(0,255,0,0.14)"
                : "rgba(255,255,255,0.03)",
              color: active ? "#0f0" : COLOR_TEXT_MUTED,
              fontFamily: "monospace",
              fontSize: FONT_SIZE_TINY,
              padding: "2px 8px",
            }}
          >
            {nextAxis.toUpperCase()}
          </Button>
        );
      })}
    </div>
  );
}
